import { useState } from "react";
import { Link } from "react-router-dom";
import { ContextBar } from "../components/ContextBar";
import { ROUTES } from "../lib/routes";
import { COMMITTEES } from "../lib/committees";
import { useScrollPastHero } from "../lib/useScrollPastHero";

const CATEGORIES = [
  { slug: "anti-ragging", label: "Ragging or hazing" },
  { slug: "icc", label: "Sexual harassment (POSH)" },
  { slug: "grievance", label: "Academic / administrative grievance" },
  { slug: "sc-st", label: "Caste-related concern (SC/ST)" },
  { slug: "womens-cell", label: "Gender equity / women's safety" },
  { slug: "equal-opportunity", label: "Accessibility / equal opportunity" },
  { slug: "", label: "Something else" },
];

export default function ReportConcern() {
  const [heroRef, scrolledPast] = useScrollPastHero<HTMLElement>();
  const [category, setCategory] = useState("");
  const [anon, setAnon] = useState(true);
  const [submitted, setSubmitted] = useState(false);

  const matchedCommittee = COMMITTEES.find((c) => c.slug === category);

  return (
    <article>
      <ContextBar
        visible={scrolledPast}
        tag="COMPLIANCE · CONFIDENTIAL"
        title="Report a concern"
        back={{ label: "Compliance", to: ROUTES.compliance }}
      />

      <section className="center-hero" ref={heroRef}>
        <div className="container">
          <nav className="event-crumbs" aria-label="Breadcrumb">
            <Link to={ROUTES.home}>Home</Link>
            <span aria-hidden>/</span>
            <Link to={ROUTES.compliance}>Compliance</Link>
            <span aria-hidden>/</span>
            <span>Report</span>
          </nav>
          <div className="ch-eyebrow">Anonymous reporting · 24×7</div>
          <span className="ch-short">CONFIDENTIAL</span>
          <h1 className="ch-name">Report a concern.</h1>
          <p className="ch-summary">
            Ragging, harassment, discrimination, accessibility issues, or any grievance — every report is reviewed within 24 hours by the relevant statutory committee. You can file anonymously; the committee will still act.
          </p>

          <div className="ch-stats">
            <div>
              <div className="num">24×7</div>
              <div className="lbl">Hotline staffed</div>
            </div>
            <div>
              <div className="num">24h</div>
              <div className="lbl">Initial review window</div>
            </div>
            <div>
              <div className="num">7</div>
              <div className="lbl">Days to action (anti-ragging)</div>
            </div>
            <div>
              <div className="num">90</div>
              <div className="lbl">Days max (POSH ICC)</div>
            </div>
          </div>
        </div>
      </section>

      <section className="detail-shell">
        <div className="container">
          <div className="detail-shell-grid">
            <div className="detail-body">
              <h2>What happens after you file</h2>
              <ol className="event-schedule">
                <li>
                  <span className="es-time">≤ 24h</span>
                  <span className="es-line" aria-hidden />
                  <span className="es-body"><span className="es-what">Convener of the relevant committee receives the report and acknowledges it. Anonymous reports are acknowledged via the same channel they came in on.</span></span>
                </li>
                <li>
                  <span className="es-time">≤ 7 days</span>
                  <span className="es-line" aria-hidden />
                  <span className="es-body"><span className="es-what">Initial inquiry · interviewing parties separately, with witness presence as needed.</span></span>
                </li>
                <li>
                  <span className="es-time">≤ 30 days</span>
                  <span className="es-line" aria-hidden />
                  <span className="es-body"><span className="es-what">Action taken or escalated to the appropriate higher authority. Statutory bodies (UGC, AICTE) are looped in where required.</span></span>
                </li>
                <li>
                  <span className="es-time">Always</span>
                  <span className="es-line" aria-hidden />
                  <span className="es-body"><span className="es-what">Confidentiality maintained throughout. Retaliation against a complainant is itself a reportable offence.</span></span>
                </li>
              </ol>

              <h2>File your report</h2>
              {!submitted ? (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    setSubmitted(true);
                  }}
                  style={{ display: "grid", gap: 16, maxWidth: 640 }}
                >
                  <label style={{ display: "grid", gap: 6 }}>
                    <span className="dmc-label">Category</span>
                    <select required value={category} onChange={(e) => setCategory(e.target.value)} style={inp}>
                      <option value="">Select a category…</option>
                      {CATEGORIES.map((c) => (
                        <option key={c.label} value={c.slug}>{c.label}</option>
                      ))}
                    </select>
                  </label>

                  {matchedCommittee && (
                    <div style={{ padding: 14, border: "1px dashed var(--rule)", borderLeft: "3px solid var(--accent)", fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.06em", color: "var(--text-muted)" }}>
                      Routed to: <b style={{ color: "var(--text)" }}>{matchedCommittee.name}</b><br />
                      Mandate: {matchedCommittee.mandate}<br />
                      <Link to={`${ROUTES.committees}/${matchedCommittee.slug}`} style={{ color: "var(--accent)" }}>
                        Read the committee's charter →
                      </Link>
                    </div>
                  )}

                  <label style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <input type="checkbox" checked={anon} onChange={(e) => setAnon(e.target.checked)} />
                    <span>File this report anonymously</span>
                  </label>

                  {!anon && (
                    <div style={{ display: "grid", gap: 16, gridTemplateColumns: "1fr 1fr" }}>
                      <label style={{ display: "grid", gap: 6 }}>
                        <span className="dmc-label">Your name</span>
                        <input type="text" style={inp} />
                      </label>
                      <label style={{ display: "grid", gap: 6 }}>
                        <span className="dmc-label">Email or phone</span>
                        <input type="text" placeholder="For us to follow up" style={inp} />
                      </label>
                    </div>
                  )}

                  <label style={{ display: "grid", gap: 6 }}>
                    <span className="dmc-label">When did this happen?</span>
                    <input type="text" placeholder="Date / approximate time" style={inp} />
                  </label>
                  <label style={{ display: "grid", gap: 6 }}>
                    <span className="dmc-label">Where did this happen?</span>
                    <input type="text" placeholder="Hostel block / classroom / off-campus / online" style={inp} />
                  </label>
                  <label style={{ display: "grid", gap: 6 }}>
                    <span className="dmc-label">What happened?</span>
                    <textarea required rows={6} placeholder="Describe the incident in your own words. Include names if comfortable, but it is not required." style={inp} />
                  </label>
                  <label style={{ display: "grid", gap: 6 }}>
                    <span className="dmc-label">Witnesses (optional)</span>
                    <input type="text" placeholder="Anyone else present" style={inp} />
                  </label>

                  <button type="submit" className="emc-cta" style={{ marginTop: 8 }}>
                    Submit report <span aria-hidden>→</span>
                  </button>
                  <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>
                    Demo form — no data is transmitted in this prototype. In production, reports route directly to the convener of the relevant committee with end-to-end encryption.
                  </p>
                </form>
              ) : (
                <div className="alum-advice">
                  <span className="lbl">Report received</span>
                  {anon
                    ? "Your report has been received. The relevant committee will review it within 24 hours. Since you filed anonymously, no follow-up will be sent to you — but the committee will still act on the information."
                    : "Your report has been received. The convener of the relevant committee will reach you within 24 hours through the contact details you provided."}
                </div>
              )}
            </div>

            <aside className="detail-meta-card is-accented">
              <div className="dmc-block">
                <div className="dmc-label">Anti-ragging hotline</div>
                <div className="dmc-value">1800-180-5522 (UGC) · 24×7</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">BIET hotline</div>
                <div className="dmc-value">+91 8192 233 870 · 24×7</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Anti-ragging email</div>
                <div className="dmc-value"><a href="mailto:antiragging@bietdvg.edu">antiragging@bietdvg.edu</a></div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">ICC (POSH)</div>
                <div className="dmc-value"><a href="mailto:icc@bietdvg.edu">icc@bietdvg.edu</a></div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">UGC online portal</div>
                <div className="dmc-value">antiragging.in</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">All committees</div>
                <div className="dmc-value">
                  <Link to={ROUTES.compliance}>Browse all six →</Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </article>
  );
}

const inp: React.CSSProperties = {
  padding: "12px 14px",
  border: "1px solid var(--rule)",
  fontFamily: "var(--font-sans)",
  fontSize: 15,
  background: "var(--bg)",
  color: "var(--text)",
};
